import React from 'react'
import moment from 'moment'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidCalendarAltIcon from 'LuxKit/Icons/solid/SolidCalendarAltIcon'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'

const ICON_SIZE_PX = 24
const ICON_BORDER_PX = 1
const ICON_TOTAL_PX = ICON_SIZE_PX + ICON_BORDER_PX * 2
const TIMELINE_LINE_WIDTH_PX = 6

const IconColumn = styled.div`
  width: ${rem(ICON_TOTAL_PX)};
  min-width: ${rem(ICON_TOTAL_PX)};
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
`

const LineSegmentTrack = styled.div`
  width: ${rem(TIMELINE_LINE_WIDTH_PX)};
  flex: 1;
  min-height: 0;
  background-color: ${(props) => props.theme.palette.neutral.default.six};
`

const CalendarIconWrap = styled.div`
  position: relative;
  width: ${rem(ICON_TOTAL_PX)};
  min-width: ${rem(ICON_TOTAL_PX)};
  height: ${rem(ICON_TOTAL_PX)};
  border: ${rem(ICON_BORDER_PX)} solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`

const DateLabelLine = styled.div`
  min-height: ${rem(ICON_TOTAL_PX)};
  display: flex;
  align-items: center;
`

const CancellationDateItem = styled(Group)`
  margin-bottom: ${rem(16)};
`

interface Props {
  today: moment.Moment
  checkInDate: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment
  cancellationAfterChangeOfMindPeriod: boolean
  /** Whether this is a charter bundle (flights + hotel). Affects terminology and shows charter-specific fees. */
  isCharter?: boolean
}

function FlexibleCancellationPolicyModalWithDates(props: Props) {
  const {
    today,
    checkInDate,
    startBasedOnToday,
    startBasedOnCheckIn,
    cancellationAfterChangeOfMindPeriod,
    isCharter,
  } = props

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  // For better readability
  const fullRefundDate = startBasedOnToday
  const partialRefundDate = moment(startBasedOnToday).add(1, 'day')
  const partialRefundDeadline = startBasedOnCheckIn
  const noRefundDate = moment(startBasedOnCheckIn).add(1, 'day')

  const formatDateForPolicy = (date: string | moment.Moment) => {
    const parsedDate = moment(date)
    const format = parsedDate.year() !== today.year() ? INTL_DMY_CASUAL_SHORT_FORMAT : INTL_DAY_MONTH_NAME_SHORT
    return intlDateFormatter(parsedDate, format)
  }

  const checkInDateLabel = isCharter
    ? intl.formatMessage(
        {
          id: 'jdUCuaxcQF-NeXnLagHX',
          defaultMessage: '{date} · Departure',
          description: 'Check-in date with departure label on cancellation timeline (charter)',
        },
        { date: formatDateForPolicy(checkInDate) },
      )
    : intl.formatMessage(
        {
          id: 'd59NymZ4PuZiqmggDhvS',
          defaultMessage: '{date} · Check-in',
          description: 'Check-in date with check-in label on cancellation timeline (non-charter)',
        },
        { date: formatDateForPolicy(checkInDate) },
      )

  const isPartialRefundToday = partialRefundDate.isSame(today, 'day')

  const upToDateLabel = intl.formatMessage(
    {
      id: 'l3pEXYwwkXfvG_HOiItm',
      defaultMessage: 'Up to {date}',
      description: 'Date label showing the end of the full refund period in cancellation timeline',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  const upToDateTodayLabel = intl.formatMessage(
    {
      id: '3WWQUkwCPrh4pVS0MjbK',
      defaultMessage: 'Up to {date} · Today',
      description: 'Date label showing end of full refund period when it is today in cancellation timeline',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  const partialRefundDateTodayLabel = intl.formatMessage(
    {
      id: 'J2kDJu22K6pdAWrxdqQV',
      defaultMessage: '{date} · Today',
      description: 'Partial refund date with today indicator on cancellation timeline',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  return (
    <>
      <Group direction="vertical">
        <Group direction="horizontal" gap={16}>
          <IconColumn>
            <CalendarIconWrap>
              <SolidCalendarAltIcon size="XS" />
            </CalendarIconWrap>
            <LineSegmentTrack />
          </IconColumn>
          <CancellationDateItem direction="vertical">
            <DateLabelLine>
              <BodyText variant="small" colour="neutral-two">
                {isPartialRefundToday ? upToDateTodayLabel : upToDateLabel}
              </BodyText>
            </DateLabelLine>
            <div>
              <BodyText variant="medium" weight="bold" colour="highlight-secondary">
                {intl.formatMessage({
                  id: 'IPvnv3wz6i5O1pItlNwB',
                  defaultMessage: 'Full refund',
                  description: 'Section heading for full refund in no-dates cancellation modal',
                })}
              </BodyText>
              <BodyText variant="medium" as="p">
                {isCharter && intl.formatMessage({
                  id: 'jhe_gCUlHNGZxsCU6At0',
                  defaultMessage: 'Cancel before {date}, 11:59 pm to get full refund to your original payment method or credit.',
                  description: 'Full refund body in with-dates cancellation modal (charter)',
                }, { date: formatDateForPolicy(fullRefundDate) })}
                {!isCharter && intl.formatMessage({
                  id: 'YbSKjtELmIvBoE3NWzzr',
                  defaultMessage: 'Cancel before {date}, 11:59 pm to get full refund of accommodation purchase price to your original payment method or credit.',
                  description: 'Full refund body in with-dates cancellation modal (non-charter)',
                }, { date: formatDateForPolicy(fullRefundDate) })}
              </BodyText>
            </div>
          </CancellationDateItem>
        </Group>

        {cancellationAfterChangeOfMindPeriod && !isCharter && (
          <Group direction="horizontal" gap={16}>
            <IconColumn>
              <CalendarIconWrap>
                <SolidCalendarAltIcon size="XS" />
              </CalendarIconWrap>
              <LineSegmentTrack />
            </IconColumn>
            <CancellationDateItem direction="vertical">
              <DateLabelLine>
                <BodyText variant="small" colour="neutral-two">
                  {isPartialRefundToday ? partialRefundDateTodayLabel : formatDateForPolicy(partialRefundDate)}
                </BodyText>
              </DateLabelLine>
              <div>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'KsmgOLy4pOps0C4ljO4T',
                    defaultMessage: 'Partial refund',
                    description: 'Section heading for partial/credit refund in a modal dialog',
                  })}
                </BodyText>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'NMl5qQRos3BLleHcfVU-',
                    defaultMessage: 'Cancel before {date}, 11:59 pm to receive 100% refund to credit.',
                    description: 'Credit-only refund for non-charter with date in the cancellation flow',
                  }, { date: formatDateForPolicy(partialRefundDeadline) })}
                </BodyText>
              </div>
            </CancellationDateItem>
          </Group>
        )}

        {cancellationAfterChangeOfMindPeriod && isCharter && (
          <Group direction="horizontal" gap={16}>
            <IconColumn>
              <CalendarIconWrap>
                <SolidCalendarAltIcon size="XS" />
              </CalendarIconWrap>
              <LineSegmentTrack />
            </IconColumn>
            <CancellationDateItem direction="vertical">
              <DateLabelLine>
                <BodyText variant="small" colour="neutral-two">
                  {isPartialRefundToday ? partialRefundDateTodayLabel : formatDateForPolicy(partialRefundDate)}
                </BodyText>
              </DateLabelLine>
              <div>
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage({
                    id: 'KsmgOLy4pOps0C4ljO4T',
                    defaultMessage: 'Partial refund',
                    description: 'Section heading for partial/credit refund in a modal dialog',
                  })}
                </BodyText>
                <Group direction="vertical" gap={8}>
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'UCWntp5VmYKW3qLQ9ovG',
                      defaultMessage: 'Cancel before {date}, 11:59 pm to get a partial refund to your original payment method or credit. Cancellation fees apply:',
                      description: 'Partial refund intro for charter with date in the payment section',
                    }, { date: formatDateForPolicy(partialRefundDeadline) })}
                  </BodyText>
                  <FlexibleCancellationCharterFlightFees />
                </Group>
              </div>
            </CancellationDateItem>
          </Group>
        )}

        <Group direction="horizontal" gap={16}>
          <IconColumn>
            <CalendarIconWrap>
              <SolidCalendarAltIcon size="XS" />
            </CalendarIconWrap>
            <LineSegmentTrack />
          </IconColumn>
          <CancellationDateItem direction="vertical">
            <DateLabelLine>
              <BodyText variant="small" colour="neutral-two">
                {formatDateForPolicy(noRefundDate)}
              </BodyText>
            </DateLabelLine>
            <div>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'yAazKuRHWgNT7wkPi4c9',
                  defaultMessage: 'No refund',
                  description: 'Section heading for no-refund period in a modal dialog',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'ah8w_Ec4UjW_D49bKyOc',
                  defaultMessage: 'Cancellations after {date}, 11:59 pm are non-refundable.',
                  description: 'No refund body in with-dates cancellation modal in a modal dialog',
                }, { date: formatDateForPolicy(partialRefundDeadline) })}
              </BodyText>
            </div>
          </CancellationDateItem>
        </Group>

        <Group direction="horizontal" gap={16} verticalAlign="center">
          <IconColumn>
            <CalendarIconWrap>
              <SolidPlaneDepartureIcon size="XS" />
            </CalendarIconWrap>
          </IconColumn>
          <BodyText variant="small" colour="neutral-two">
            {checkInDateLabel}
          </BodyText>
        </Group>
      </Group>

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'zOgZxgWqO_0Kba00Iaqk',
            defaultMessage: 'Date changes',
            description: 'Section heading for date changes in the date selection or calendar picker',
          })}
        </BodyText>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: '7faDfrrNU0w8A0OdSY9Z',
            defaultMessage: 'One change permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
            description: 'Date change policy text with date range in the date selection or calendar picker',
          }, {
            startDate: formatDateForPolicy(fullRefundDate),
            endDate: partialRefundDeadline ? formatDateForPolicy(partialRefundDeadline) : '',
          })}
        </BodyText>
      </div>
    </>
  )
}

export default FlexibleCancellationPolicyModalWithDates
